import { formatInTimeZone } from 'date-fns-tz';
import {
  CATS_MER, PRESENCIA_MER, esMarcaPg, etiquetaCat, etiquetaPresencia,
} from '../../../domain/pg/catalogo';
import {
  contradiccionesDeFicha, marcaDePrecio, precioPorUnidad,
} from '../../../domain/pg/mercados';
import { claveTexto, STOPWORDS_LUGAR, type GrupoMercado } from '../../../domain/pg/normalizar';
import type { CierreMercado, FichaMercado, FotoPg } from '../../../domain/pg/tipos';
import {
  Dato, FotoPrivada, Seccion, SeccionPlegable, Senal,
} from '../../../components/DetalleExpandible';

const TZ = 'America/Lima';
const SIN = <span className="text-gray-400">—</span>;

const texto = (v: string | null | undefined) => {
  const t = (v ?? '').trim();
  return t === '' ? SIN : <>{t}</>;
};

const mmss = (s: number | null): string => {
  if (s === null) return '—';
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

function Fotos({ fotos, alt }: { fotos: readonly FotoPg[] | undefined; alt: string }) {
  const lista = fotos ?? [];
  if (lista.length === 0) return <p className="text-sm text-gray-500">Sin fotos.</p>;
  return (
    <div className="flex flex-wrap gap-3">
      {lista.map((f, i) => {
        const ruta = (f.ruta ?? '').trim();
        if (ruta === '') {
          // Una foto con `error` y sin `ruta` NO es «sin foto»: es una foto que el campo sacó y que
          // no llegó a subir. La ficha se envió igual a propósito —ninguna foto traba el envío— y
          // quien revisa tiene que poder distinguir las dos cosas.
          return (
            <p key={i} className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
              Se tomó una foto y no se pudo subir{f.error != null && f.error !== '' ? `: ${f.error}` : '.'}
            </p>
          );
        }
        return <FotoPrivada key={ruta} path={ruta} alt={`${alt} ${i + 1}`} />;
      })}
    </div>
  );
}

/**
 * El súper detalle de una ficha de mercado: todo lo que se levantó, rotulado **con el texto exacto
 * de la pregunta que vio la auditora**.
 *
 * Los rótulos no son una elección de estilo. Quien revisa acá es quien después llama al supervisor,
 * y si la pantalla dice «Abastecimiento» donde el celular decía «¿De dónde saca el producto?», los
 * dos hablan de cosas que creen distintas. El precio de copiar los textos es cero; el de no
 * copiarlos se paga en cada conversación.
 *
 * Las secciones largas —presencia, precios, promociones— **nacen plegadas con su conteo a la
 * vista**: un puesto trae hasta 44 marcas y 40 precios, y dibujados todos abiertos el panel deja de
 * ser un detalle y pasa a ser un volcado que nadie lee.
 */
export default function DetalleMercado({ ficha: f, cierres, grupo }: {
  ficha: FichaMercado;
  cierres: readonly CierreMercado[];
  grupo: GrupoMercado | null;
}) {
  const j = f.ficha;
  const cats = j.cats ?? [];
  const presencia = j.presencia ?? {};
  const precios = j.precios ?? [];
  const promos = j.promos ?? [];
  const contras = contradiccionesDeFicha(f);

  const claveMercado = claveTexto(f.mercadoNombre, STOPWORDS_LUGAR);
  const cierre = cierres.find(
    (c) => claveTexto(c.mercadoNombre, STOPWORDS_LUGAR) === claveMercado && c.fecha === f.fecha,
  ) ?? null;

  const preciosPorCat = CATS_MER
    .map((c) => ({ cat: c, filas: precios.filter((p) => p.cat === c.id) }))
    .filter((x) => x.filas.length > 0);
  // Precios de una categoría que la ficha no declaró: el formulario avisa pero deja enviar, y no
  // borra nada. Existen de verdad, así que se muestran; simplemente no pertenecen a ningún bloque.
  const preciosSueltos = precios.filter((p) => !CATS_MER.some((c) => c.id === p.cat));

  return (
    <>
      {contras.length > 0 && (
        <div className="space-y-2">
          {contras.map((c) => (
            <p key={c.clave}
              className={`rounded border px-3 py-2 text-sm ${c.tono === 'grave'
                ? 'border-red-200 bg-red-50 text-red-900'
                : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
              {c.texto}
            </p>
          ))}
        </div>
      )}

      <Seccion titulo="Identificación · ¿Qué puesto es?" ancho>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <Dato etiqueta="Número del puesto en tu recorrido">
            {f.puestoNumero === null ? SIN : `#${f.puestoNumero}`}
          </Dato>
          <Dato etiqueta="Nombre o seña del puesto (opcional)">{texto(f.puestoNombre)}</Dato>
          <Dato etiqueta="¿Pudiste levantar este puesto?">
            {f.levantado ? 'Sí, lo levanté' : 'No pude'}
          </Dato>
          {!f.levantado && (
            <Dato etiqueta="¿Por qué no?">
              {(f.motivoNo ?? '').trim() === ''
                ? <Senal>sin motivo</Senal>
                : f.motivoNo}
            </Dato>
          )}
          <Dato etiqueta="Supervisor">{texto(f.supervisor)}</Dato>
          <Dato etiqueta="Dirección o referencia">{texto(f.mercadoDireccion)}</Dato>
          <Dato etiqueta="Mercado, como se escribió">{texto(f.mercadoNombre)}</Dato>
          <Dato etiqueta="Mercado, agrupado">
            {grupo?.rotulo ?? SIN}
            {grupo?.sinNormalizar === true && <> <Senal>sin normalizar</Senal></>}
          </Dato>
          <Dato etiqueta="¿Se escribió a mano?">
            {j.mercado_escrito_a_mano === true ? 'Sí, se tecleó' : 'No, salió de la lista'}
          </Dato>
          <Dato etiqueta="Zona de Lima">{texto(f.zona)}</Dato>
          <Dato etiqueta="Distrito">{texto(f.distrito)}</Dato>
          <Dato etiqueta="Coordenadas">
            {f.lat === null || f.lng === null ? <Senal tono="neutro">sin GPS</Senal> : (
              <a
                className="text-blue-700 underline"
                href={`https://www.google.com/maps?q=${f.lat},${f.lng}`}
                target="_blank" rel="noreferrer"
              >
                {f.lat.toFixed(5)}, {f.lng.toFixed(5)}
                {f.accuracy !== null && ` (±${Math.round(f.accuracy)} m)`}
              </a>
            )}
          </Dato>
          <Dato etiqueta="Cuánto tardó">{mmss(f.duracionSeg)}</Dato>
          <Dato etiqueta="Se envió">{f.origin === 'offline' ? 'sin señal, desde la cola' : 'con señal'}</Dato>
          <Dato etiqueta="Llegó al servidor">
            {f.registradoEn === null ? SIN
              : formatInTimeZone(new Date(f.registradoEn), TZ, 'dd/MM/yyyy HH:mm')}
          </Dato>
        </dl>
      </Seccion>

      {/* 🔴 Un puesto no levantado NO muestra ocho secciones vacías. El formulario solo le enseña
          el bloque 1: los bloques 2 al 9 nunca se le preguntaron. «Vacío» y «no corresponde» son
          cosas distintas, y ocho vacíos seguidos se leen como un registro incompleto cuando es un
          registro completo de un puesto cerrado. */}
      {!f.levantado && cats.length === 0 && precios.length === 0 && (
        <p className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          Este puesto no se pudo medir, así que del bloque 2 en adelante no se preguntó nada. No
          falta información: no corresponde.
        </p>
      )}

      {(f.levantado || cats.length > 0) && (
        <Seccion titulo="El recorte · ¿Qué vende este puesto?" extra={`${cats.length} categorías`} ancho>
          {cats.length === 0 ? <p className="text-sm text-gray-500">No se marcó ninguna.</p> : (
            <div className="flex flex-wrap gap-1">
              {cats.map((c) => <Senal key={c} tono="neutro">{etiquetaCat(c)}</Senal>)}
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Esto recorta todo lo que sigue: solo se preguntaron las marcas y los precios de lo
            marcado acá.
          </p>
        </Seccion>
      )}

      {(j.clientes ?? []).length > 0 && (
        <Seccion titulo="El negocio · ¿A quién le vende más este puesto?" ancho>
          <div className="flex flex-wrap gap-1">
            {(j.clientes ?? []).map((c) => <Senal key={c} tono="neutro">{c}</Senal>)}
          </div>
        </Seccion>
      )}

      {Object.keys(presencia).length > 0 && (
        <SeccionPlegable
          titulo="Disponibilidad · Marca por marca"
          extra={<span className="text-xs text-gray-500">{Object.keys(presencia).length} marcas</span>}
          ancho
        >
          <p className="mb-2 text-xs text-gray-600">
            <b>Se le acabó</b> no es lo mismo que <b>no la trabaja</b>: la primera es una venta
            perdida esta semana, la segunda es una marca que nunca entró al puesto.
          </p>
          <div className="space-y-3">
            {CATS_MER.filter((c) => [...c.pg, ...c.comp].some((m) => presencia[m] !== undefined))
              .map((c) => (
                <div key={c.id}>
                  <h5 className="text-xs font-semibold text-gray-700">
                    {c.nom}
                    {!cats.includes(c.id) && (
                      <> <Senal>la categoría quedó desmarcada</Senal></>
                    )}
                  </h5>
                  <ul className="mt-1 grid gap-x-6 gap-y-1 sm:grid-cols-2">
                    {[...c.pg, ...c.comp].filter((m) => presencia[m] !== undefined).map((m) => {
                      const v = presencia[m];
                      const tono = PRESENCIA_MER.find((p) => p.valor === v)?.tono
                        ?? 'bg-gray-100 text-gray-700';
                      return (
                        <li key={`${c.id}-${m}`} className="flex items-baseline justify-between gap-2 text-sm">
                          <span>
                            {m}{' '}
                            <span className="text-xs text-gray-500">
                              {esMarcaPg(m) ? 'P&G' : 'competencia'}
                            </span>
                          </span>
                          <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${tono}`}>
                            {etiquetaPresencia(v)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
          </div>
        </SeccionPlegable>
      )}

      {precios.length > 0 && (
        <SeccionPlegable
          titulo="Precio · Precios y presentaciones"
          extra={<span className="text-xs text-gray-500">{precios.length} filas</span>}
          ancho
        >
          <div className="space-y-4">
            {[...preciosPorCat, ...(preciosSueltos.length > 0
              ? [{ cat: { id: '?', nom: 'Sin categoría reconocida' }, filas: preciosSueltos }]
              : [])].map(({ cat, filas }) => (
              <div key={cat.id}>
                <h5 className="mb-1 text-xs font-semibold text-gray-700">{cat.nom}</h5>
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="py-1 pr-3">Marca</th>
                      <th className="py-1 pr-3">Presentación</th>
                      <th className="py-1 pr-3 text-right">Precio (S/)</th>
                      <th className="py-1 pr-3 text-right">¿Cuántas unidades trae?</th>
                      <th className="py-1 text-right">Por unidad</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filas.map((p, i) => {
                      const { nombre, esMarca } = marcaDePrecio(p);
                      const pu = precioPorUnidad(p.precio, p.unidades);
                      const est = esMarca ? presencia[nombre] : undefined;
                      return (
                        <tr key={i}>
                          <td className="py-1 pr-3">
                            {esMarca ? nombre : <span className="text-gray-500">{nombre}</span>}
                            {esMarca && est !== 'presente' && (
                              <> <Senal tono="grave">
                                {est === undefined ? 'sin respuesta de presencia' : etiquetaPresencia(est)}
                              </Senal></>
                            )}
                          </td>
                          <td className="py-1 pr-3">{texto(p.present)}</td>
                          <td className="py-1 pr-3 text-right tabular-nums">
                            {p.precio === null ? SIN : p.precio.toFixed(2)}
                          </td>
                          <td className="py-1 pr-3 text-right tabular-nums">
                            {p.unidades === null ? SIN : p.unidades}
                          </td>
                          <td className="py-1 text-right tabular-nums">
                            {pu !== null ? `S/ ${pu.toFixed(2)}`
                              : p.unidades === 1
                                // Cargado como «1 unidad»: el número de la izquierda es el precio
                                // del paquete. Decirlo acá evita que se lea como precio unitario.
                                ? <span className="text-xs text-gray-500">precio del paquete</span>
                                : SIN}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            El precio por unidad no se guarda: se recalcula acá. Solo existe cuando la presentación
            trae más de una unidad.
          </p>
        </SeccionPlegable>
      )}

      {promos.length > 0 && (
        <SeccionPlegable
          titulo="Promoción · Promociones a la vista"
          extra={<span className="text-xs text-gray-500">{promos.length}</span>}
          ancho
        >
          <ul className="space-y-3">
            {promos.map((p, i) => (
              <li key={i} className="rounded border border-gray-200 p-3">
                <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-4">
                  <Dato etiqueta="¿De quién es?">{texto(p.dequien)}</Dato>
                  <Dato etiqueta="Marca">{texto(p.marca)}</Dato>
                  <Dato etiqueta="Mecánica">{texto(p.mecanica)}</Dato>
                  <Dato etiqueta="Qué dice el cartel">{texto(p.texto)}</Dato>
                </dl>
                {(p.fotos ?? []).length > 0 && (
                  <div className="mt-2"><Fotos fotos={p.fotos} alt={`Promoción ${i + 1}`} /></div>
                )}
              </li>
            ))}
          </ul>
        </SeccionPlegable>
      )}

      {j.abasto !== undefined && (
        <Seccion titulo="Bróker · De dónde saca el producto" ancho>
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Dato etiqueta="¿De dónde saca el producto?">
              {(j.abasto.donde ?? []).length === 0 ? SIN : (
                <div className="flex flex-wrap gap-1">
                  {(j.abasto.donde ?? []).map((d) => <Senal key={d} tono="neutro">{d}</Senal>)}
                </div>
              )}
            </Dato>
            <Dato etiqueta="¿Cada cuánto le llega?">{texto(j.abasto.frecuencia)}</Dato>
            <Dato etiqueta="¿Lo visita algún vendedor o impulsador de marca?">
              {/* Los cuatro estados son distintos y el formulario los guarda distinto a propósito:
                  `''` es «no se preguntó» y `no_dijo` es «no quiso decir». Colapsarlos convierte
                  una pregunta sin hacer en una negativa. */}
              {j.abasto.visita === 'si' ? 'Sí'
                : j.abasto.visita === 'no' ? 'No'
                  : j.abasto.visita === 'no_dijo' ? 'No quiso decir'
                    : <span className="text-gray-500">no se preguntó</span>}
            </Dato>
            {j.abasto.visita === 'si' && (
              <Dato etiqueta="¿De qué marca?">{texto(j.abasto.visita_quien)}</Dato>
            )}
            <Dato etiqueta="¿Cómo se llama el distribuidor o bróker? (opcional)">
              {texto(j.abasto.quien)}
            </Dato>
          </dl>
        </Seccion>
      )}

      {j.recomienda !== undefined && (j.recomienda.marca ?? '') !== '' && (
        <Seccion
          titulo="Recomendación · Si entra un cliente y no pide marca, ¿cuál le ofrece primero?"
          ancho
        >
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Dato etiqueta="Marca">
              {j.recomienda.marca === 'Otra'
                ? texto(j.recomienda.marca_otra)
                : texto(j.recomienda.marca)}
            </Dato>
            <Dato etiqueta="¿Por qué esa?">{texto(j.recomienda.motivo)}</Dato>
          </dl>
        </Seccion>
      )}

      <Seccion titulo="Cierre · Foto y lo que no estaba" ancho>
        <div className="space-y-3">
          <Fotos fotos={j.cierre?.fotos} alt="Foto del puesto" />
          <dl className="grid gap-x-6 gap-y-2">
            <Dato etiqueta="Marcas que viste y no estaban en la lista">
              {texto(j.cierre?.fuera_de_lista)}
            </Dato>
            <Dato etiqueta="Algo más de este puesto">{texto(j.cierre?.obs)}</Dato>
          </dl>
        </div>
      </Seccion>

      <Seccion titulo="Cierre de jornada del mercado" ancho>
        {cierre === null ? (
          <p className="text-sm text-gray-600">
            Este mercado-día no tiene cierre de jornada registrado. Es información, no un hueco: el
            cierre lo llena el supervisor una vez por mercado y día.
          </p>
        ) : (
          <>
            {cierre.mercadoNombre !== f.mercadoNombre && (
              <p className="mb-2 text-xs text-gray-500">
                Se cerró como «{cierre.mercadoNombre}».
              </p>
            )}
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              <Dato etiqueta="¿Cómo estuvo la afluencia hoy?">{texto(cierre.afluencia)}</Dato>
              <Dato etiqueta="¿Hoy era día con alguna actividad adicional?">
                {cierre.actividadAdicional === null ? SIN : (cierre.actividadAdicional ? 'Sí' : 'No')}
              </Dato>
              <Dato etiqueta="¿Pasó algo que explique el día?">{texto(cierre.factorExterno)}</Dato>
              <Dato etiqueta="Competencia en el mercado">{texto(cierre.competenciaActiva)}</Dato>
              <Dato etiqueta="Algo más que quieras dejar dicho">{texto(cierre.observacion)}</Dato>
              <Dato etiqueta="Lo cerró">{texto(cierre.supervisor)}</Dato>
            </dl>
          </>
        )}
      </Seccion>
    </>
  );
}
